import type { DecodedMapTile, MapTileIndexEntry } from "./schema";

interface WorkerMessageEvent<T> {
  data: T;
}

export interface MapTileWorkerPort {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: WorkerMessageEvent<unknown>) => void): void;
  removeEventListener(
    type: "message",
    listener: (event: WorkerMessageEvent<unknown>) => void,
  ): void;
  terminate(): void;
}

interface DecodedResponse {
  type: "decoded";
  key: string;
  id: string;
  generation: number;
  tile: DecodedMapTile;
  fetchMs: number;
  decodeMs: number;
}

interface ErrorResponse {
  type: "error";
  key: string;
  id: string;
  generation: number;
  message: string;
}

type WorkerResponse = DecodedResponse | ErrorResponse;

interface PendingLoad {
  id: string;
  generation: number;
  signal: AbortSignal;
  abort: () => void;
  resolve: (value: { tile: DecodedMapTile; fetchMs: number; decodeMs: number }) => void;
  reject: (reason: unknown) => void;
}

function isWorkerResponse(value: unknown): value is WorkerResponse {
  if (!value || typeof value !== "object") return false;
  const response = value as Partial<WorkerResponse>;
  return (
    (response.type === "decoded" || response.type === "error") &&
    typeof response.key === "string" &&
    typeof response.id === "string" &&
    typeof response.generation === "number"
  );
}

export class MapTileLoader {
  readonly #port: MapTileWorkerPort;
  readonly #pending = new Map<string, PendingLoad>();
  #disposed = false;

  constructor(port?: MapTileWorkerPort) {
    this.#port =
      port ??
      (new Worker(new URL("./map-tile.worker.ts", import.meta.url), {
        type: "module",
      }) as MapTileWorkerPort);
    this.#port.addEventListener("message", this.#onMessage);
  }

  load(
    entry: MapTileIndexEntry,
    generation: number,
    signal: AbortSignal,
  ): Promise<{ tile: DecodedMapTile; fetchMs: number; decodeMs: number }> {
    if (this.#disposed) return Promise.reject(new Error("MapTileLoader is disposed"));
    const key = `${generation}:${entry.id}`;
    if (this.#pending.has(key))
      return Promise.reject(new Error(`Map tile request ${key} is already pending`));
    const { promise, resolve, reject } = Promise.withResolvers<{
      tile: DecodedMapTile;
      fetchMs: number;
      decodeMs: number;
    }>();
    const abort = (): void => {
      const pending = this.#pending.get(key);
      if (!pending) return;
      this.#pending.delete(key);
      signal.removeEventListener("abort", abort);
      this.#port.postMessage({ type: "abort", key });
      reject(new DOMException(`Map tile request ${key} aborted`, "AbortError"));
    };
    const pending: PendingLoad = {
      id: entry.id,
      generation,
      signal,
      abort,
      resolve,
      reject,
    };
    this.#pending.set(key, pending);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) {
      abort();
      return promise;
    }
    this.#port.postMessage({
      type: "load",
      key,
      id: entry.id,
      generation,
      url: `/world/${entry.url}`,
    });
    return promise;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#port.removeEventListener("message", this.#onMessage);
    for (const [key, pending] of this.#pending) {
      pending.signal.removeEventListener("abort", pending.abort);
      this.#port.postMessage({ type: "abort", key });
      pending.reject(new DOMException("Map tile loader disposed", "AbortError"));
    }
    this.#pending.clear();
    this.#port.terminate();
  }

  readonly #onMessage = (event: WorkerMessageEvent<unknown>): void => {
    if (!isWorkerResponse(event.data)) return;
    const response = event.data;
    const pending = this.#pending.get(response.key);
    if (!pending || pending.id !== response.id || pending.generation !== response.generation)
      return;
    this.#pending.delete(response.key);
    pending.signal.removeEventListener("abort", pending.abort);
    if (response.type === "error") {
      pending.reject(new Error(response.message));
      return;
    }
    pending.resolve({
      tile: response.tile,
      fetchMs: response.fetchMs,
      decodeMs: response.decodeMs,
    });
  };
}
